import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Power, Lightbulb, Check } from 'lucide-react';
const PLAIN_CAPABILITIES = [{
  id: 'always-on',
  title: 'Works while you sleep',
  desc: 'Giga works around the clock handling tasks on your behalf: scheduled jobs and recurring actions.',
  icon: Power
}, {
  id: 'memory',
  title: 'Never repeat yourself',
  desc: 'Every conversation, every preference, every goal, carried across sessions. Giga picks up where it left off and refines its own rules.',
  icon: Lightbulb
}];
function ScheduleVisual() {
  return <div className="relative w-full h-full min-h-[240px] bg-[#2B211A] p-6 md:p-8 flex flex-col overflow-hidden" data-id="element-562">
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_top,rgba(234,88,12,0.15),transparent_70%)] pointer-events-none" data-id="element-563" />

      {/* App Message */}
      <div className="relative z-10 mb-8" data-id="element-564">
        <div className="flex items-center gap-2 mb-1.5" data-id="element-565">
          <span className="text-white font-semibold text-sm" data-id="element-566">Giga</span>
          <span className="text-[10px] font-bold tracking-wider text-[#EA580C] bg-[#EA580C]/10 px-1.5 py-0.5 rounded uppercase" data-id="element-567">
            App
          </span>
        </div>
        <p className="text-white/90 text-sm md:text-base" data-id="element-568">
          Posted your Monday recap. Same time next week.
        </p>
      </div>

      {/* Visual Elements */}
      <div className="relative z-10 flex-1 flex items-center justify-center mt-4" data-id="element-569">
        <div className="relative flex items-center w-full max-w-[280px]" data-id="element-570">
          {/* Main Icon */}
          <div className="relative z-20 flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-xl shrink-0" data-id="element-571">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-572">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" data-id="element-573" />
              <line x1="16" y1="2" x2="16" y2="6" data-id="element-574" />
              <line x1="8" y1="2" x2="8" y2="6" data-id="element-575" />
              <line x1="3" y1="10" x2="21" y2="10" data-id="element-576" />
              <rect x="8" y="14" width="4" height="4" rx="1" data-id="element-577" />
            </svg>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#22C55E] border-2 border-[#2B211A] flex items-center justify-center text-white" data-id="element-578">
              <Check size={10} strokeWidth={4} data-id="element-579" />
            </div>
          </div>

          {/* Floating Pills */}
          <div className="relative z-10 flex flex-col gap-3 -ml-4 w-full" data-id="element-580">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative pl-8 pr-4 py-2.5 rounded-r-xl bg-white/[0.06] border border-white/10 backdrop-blur-sm" data-id="element-581">
              <span className="text-white/80 text-xs font-medium" data-id="element-582">
                Revenue +12% WoW
              </span>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="relative pl-8 pr-4 py-2.5 rounded-r-xl bg-white/[0.06] border border-white/10 backdrop-blur-sm w-[85%]" data-id="element-583">
              <span className="text-white/80 text-xs font-medium" data-id="element-584">
                Ad spend $4.1k
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}
function LearnsVisual() {
  return <div className="relative w-full h-full min-h-[240px] bg-[#2B211A] p-6 md:p-8 flex flex-col overflow-hidden" data-id="element-585">
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_top,rgba(234,88,12,0.15),transparent_70%)] pointer-events-none" data-id="element-586" />

      {/* App Message */}
      <div className="relative z-10 mb-8" data-id="element-587">
        <div className="flex items-center gap-2 mb-1.5" data-id="element-588">
          <span className="text-white font-semibold text-sm" data-id="element-589">Giga</span>
          <span className="text-[10px] font-bold tracking-wider text-[#EA580C] bg-[#EA580C]/10 px-1.5 py-0.5 rounded uppercase" data-id="element-590">
            App
          </span>
        </div>
        <p className="text-white/90 text-sm md:text-base" data-id="element-591">
          Used your usual format. I remembered.
        </p>
      </div>

      {/* Visual Elements */}
      <div className="relative z-10 flex-1 flex items-center justify-center mt-4" data-id="element-592">
        <div className="relative w-full max-w-[280px] h-[120px]" data-id="element-593">
          {/* Connecting Lines */}
          <svg className="absolute inset-0 w-full h-full" style={{
          overflow: 'visible'
        }} data-id="element-594">
            <path d="M 40 40 Q 90 40 140 60" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" data-id="element-595" />
            <path d="M 80 100 Q 110 100 140 60" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" data-id="element-596" />
            <path d="M 240 40 Q 190 40 140 60" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" data-id="element-597" />
            <path d="M 220 100 Q 180 100 140 60" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2" data-id="element-598" />
          </svg>

          {/* Center Brain */}
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-xl" data-id="element-599">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-600">
              <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" data-id="element-601" />
              <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" data-id="element-602" />
            </svg>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#22C55E] border-2 border-[#2B211A] flex items-center justify-center text-white" data-id="element-603">
              <Check size={10} strokeWidth={4} data-id="element-604" />
            </div>
          </motion.div>

          {/* Satellite Nodes */}
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="absolute top-4 left-4 flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-605">
            <div className="w-4 h-4 rounded-[4px] border-2 border-white/40" data-id="element-606" />
          </motion.div>

          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="absolute bottom-2 left-14 flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-607">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-608">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" data-id="element-609" />
            </svg>
          </motion.div>

          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-610">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-611">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-612" />
              <polyline points="14 2 14 8 20 8" data-id="element-613" />
              <line x1="16" y1="13" x2="8" y2="13" data-id="element-614" />
              <line x1="16" y1="17" x2="8" y2="17" data-id="element-615" />
              <polyline points="10 9 9 9 8 9" data-id="element-616" />
            </svg>
          </motion.div>

          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="absolute bottom-2 right-14 flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-617">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-618">
              <rect width="20" height="16" x="2" y="4" rx="2" data-id="element-619" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-620" />
            </svg>
          </motion.div>
        </div>
      </div>
    </div>;
}
export function CapabilityGrid() {
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32" data-id="element-621">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-622">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-20" data-id="element-623">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-624">
            Action over answers
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] md:whitespace-nowrap" data-id="element-625">
            Built to do the work — not just talk about it.
          </h2>
        </div>

        <div className="max-w-5xl mx-auto" data-id="element-626">
          {/* Top Row: Plain Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16" data-id="element-627">
            {PLAIN_CAPABILITIES.map((cap, index) => {
            const Icon = cap.icon;
            return <motion.div key={cap.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex flex-col items-start text-left" data-id="element-628">
                  <div className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA580C]/10 mb-6" data-id="element-629">
                    <Icon strokeWidth={1.5} className="w-6 h-6 text-[#EA580C]" data-id="element-630" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-[#2B211A]" data-id="element-631">
                    {cap.title}
                  </h3>
                  <p className="leading-relaxed text-[15px] text-neutral-500" data-id="element-632">
                    {cap.desc}
                  </p>
                </motion.div>;
          })}
          </div>

          {/* Bottom Row: Rich Visual Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-633">
            {/* Schedule Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col rounded-3xl bg-white border border-neutral-200/70 shadow-sm overflow-hidden" data-id="element-634">
              <ScheduleVisual data-id="element-635" />
              <div className="p-8 md:p-10" data-id="element-636">
                <h3 className="text-xl font-semibold mb-3 text-[#2B211A]" data-id="element-637">
                  It works on a schedule
                </h3>
                <p className="leading-relaxed text-[15px] text-neutral-500" data-id="element-638">
                  Daily reports, weekly digests, all on autopilot. In your
                  channel before you ask.
                </p>
              </div>
            </motion.div>

            {/* Learns Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col rounded-3xl bg-white border border-neutral-200/70 shadow-sm overflow-hidden" data-id="element-639">
              <LearnsVisual data-id="element-640" />
              <div className="p-8 md:p-10" data-id="element-641">
                <h3 className="text-xl font-semibold mb-3 text-[#2B211A]" data-id="element-642">
                  It learns your company
                </h3>
                <p className="leading-relaxed text-[15px] text-neutral-500" data-id="element-643">
                  It remembers your decisions and preferences, so you never
                  repeat yourself.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}